/**
 * Stream Logger Service
 *
 * Captures WebRTC streaming lifecycle events in a ring buffer for inclusion
 * in bug reports. Each event is also forwarded as a Sentry breadcrumb so that
 * error reports automatically include the streaming timeline leading up to the
 * issue. When Sentry is not initialized (no DSN), the breadcrumb calls are
 * safe no-ops.
 */

import * as Sentry from '@sentry/react';

export type StreamEventType =
  | 'connect_attempt'
  | 'connect_success'
  | 'connect_failure'
  | 'disconnect'
  | 'media_track_timeout'
  | 'retry_scheduled'
  | 'ice_state_change'
  | 'resize'
  | 'resize_failure'
  | 'resize_observer_attached'
  | 'resize_observer_detached'
  | 'send_failure'
  | 'terminate_failure'
  | 'ice_failed_reconnect';

export interface StreamLogEntry {
  type: StreamEventType;
  timestamp: string;
  data: Record<string, unknown>;
}

const MAX_ENTRIES = 500;

const ERROR_EVENT_TYPES = new Set<StreamEventType>([
  'connect_failure',
  'media_track_timeout',
  'resize_failure',
  'send_failure',
  'terminate_failure',
]);

const WARNING_EVENT_TYPES = new Set<StreamEventType>(['disconnect', 'ice_failed_reconnect']);

class StreamLoggerService {
  private entries: StreamLogEntry[] = [];

  /**
   * Capture a streaming lifecycle event.
   *
   * The event is stored in the local ring buffer for bug reports and
   * forwarded to Sentry as a breadcrumb for automatic error context.
   */
  capture(type: StreamEventType, data: Record<string, unknown> = {}): void {
    const entry: StreamLogEntry = {
      type,
      timestamp: new Date().toISOString(),
      data,
    };

    this.entries.push(entry);
    if (this.entries.length > MAX_ENTRIES) {
      this.entries = this.entries.slice(-MAX_ENTRIES);
    }

    Sentry.addBreadcrumb({
      category: 'webrtc',
      message: type,
      data,
      level: ERROR_EVENT_TYPES.has(type) ? 'error' : WARNING_EVENT_TYPES.has(type) ? 'warning' : 'info',
    });
  }

  /**
   * Get all captured streaming events.
   */
  getEntries(): StreamLogEntry[] {
    return [...this.entries];
  }

  /**
   * Get only error/failure events from the buffer.
   */
  getRecentErrors(): StreamLogEntry[] {
    return this.entries.filter((entry) => ERROR_EVENT_TYPES.has(entry.type) || WARNING_EVENT_TYPES.has(entry.type));
  }

  /**
   * Clear all captured entries.
   */
  clear(): void {
    this.entries = [];
  }
}

// Singleton instance
export const streamLogger = new StreamLoggerService();
